import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTruck,
    faBoxOpen,
    faWeightHanging,
    faCheckCircle,
    faHourglass,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const StatCard = ({ label, value, sub, icon, color, bg, border }) => (
    <div
        style={{
            flex: '1 1 180px', minWidth: 170, maxWidth: 260,
            backgroundColor: '#fff',
            border: `1px solid ${border}`,
            borderRadius: 14,
            padding: '16px 18px',
            display: 'flex', flexDirection: 'column', gap: 8,
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 4px 16px ${border}bb`)}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </span>
            <div style={{
                width: 32, height: 32, borderRadius: 8,
                backgroundColor: bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color, fontSize: 13, flexShrink: 0, marginLeft: 8,
            }}>
                <FontAwesomeIcon icon={icon} />
            </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>
    </div>
);

const SectionBlock = ({ title, accent, cards }) => (
    <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <div style={{ width: 3, height: 18, borderRadius: 2, backgroundColor: accent }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</span>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
        </div>
    </div>
);

const SupervisorSummaryCards = ({ summary }) => {
    const { t } = useTranslation();
    if (!summary) return null;

    const { loading: ld, unloading: ul } = summary;

    const loadingCards = [
        {
            label: t('supervisorAnalytics.summaryCards.totalAssigned'),
            value: ld?.total_assigned ?? 0,
            sub: t('supervisorAnalytics.summaryCards.loadingOrdersAssigned'),
            icon: faTruck,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        {
            label: t('supervisorAnalytics.summaryCards.completed'),
            value: ld?.completed ?? 0,
            sub: t('supervisorAnalytics.summaryCards.successfullyLoaded'),
            icon: faCheckCircle,
            color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
        },
        {
            label: t('supervisorAnalytics.summaryCards.pending'),
            value: ld?.pending ?? 0,
            sub: t('supervisorAnalytics.summaryCards.yetToBeLoaded'),
            icon: faHourglass,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
        {
            label: t('supervisorAnalytics.summaryCards.weightLoaded'),
            value: `${parseFloat(ld?.total_weight_loaded_tons ?? 0).toFixed(2)} T`,
            sub: `${(ld?.total_weight_loaded_kg ?? 0).toLocaleString()} kg`,
            icon: faWeightHanging,
            color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
        },
    ];

    const unloadingCards = [
        {
            label: t('supervisorAnalytics.summaryCards.totalAssigned'),
            value: ul?.total_assigned ?? 0,
            sub: t('supervisorAnalytics.summaryCards.unloadingOrdersAssigned'),
            icon: faBoxOpen,
            color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        },
        {
            label: t('supervisorAnalytics.summaryCards.completed'),
            value: ul?.completed ?? 0,
            sub: t('supervisorAnalytics.summaryCards.successfullyUnloaded'),
            icon: faCheckCircle,
            color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
        },
        {
            label: t('supervisorAnalytics.summaryCards.pending'),
            value: ul?.pending ?? 0,
            sub: t('supervisorAnalytics.summaryCards.yetToBeUnloaded'),
            icon: faHourglass,
            color: '#d97706', bg: '#fffbeb', border: '#fde68a',
        },
        {
            label: t('supervisorAnalytics.summaryCards.weightUnloaded'),
            value: `${parseFloat(ul?.total_weight_unloaded_tons ?? 0).toFixed(2)} T`,
            sub: `${(ul?.total_weight_unloaded_kg ?? 0).toLocaleString()} kg`,
            icon: faWeightHanging,
            color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
        },
    ];

    return (
        <div>
            <SectionBlock title={t('supervisorAnalytics.summaryCards.loadingActivity')} accent="#0891b2" cards={loadingCards} />
            <SectionBlock title={t('supervisorAnalytics.summaryCards.unloadingActivity')} accent="#7c3aed" cards={unloadingCards} />
        </div>
    );
};

export default SupervisorSummaryCards;
